import { FC, MouseEvent } from 'react'

import { useNavigate } from '@tanstack/react-router'

import { Button, Icon, Tooltip, TooltipContent, TooltipTrigger } from '@forto-dev/phoenix-ui-core'

import { useLocalisation } from '@/utils/localisation'

export type BookAgainSource = { type: 'shipment'; code: string } | { type: 'booking'; id: string }

type BookAgainButtonProps = {
  source: BookAgainSource
  /** Render a compact icon button with a tooltip (e.g. for dense list rows). */
  compact?: boolean
}

const useText = () => {
  const { translate } = useLocalisation()
  return {
    action: translate('book_again.action'),
  }
}

export const BookAgainButton: FC<BookAgainButtonProps> = ({ source, compact = false }) => {
  const text = useText()
  const navigate = useNavigate()

  // Entry points such as the overview list row wrap the trigger in a navigating link.
  const handleClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    navigate({
      to: '/bookings/create',
      search:
        source.type === 'shipment' ? { bookAgainFromShipmentCode: source.code } : { bookAgainFromBookingID: source.id },
    })
  }

  if (compact) {
    return (
      <Tooltip>
        <TooltipTrigger>
          <Button
            variant="outline-secondary"
            size="icon"
            onClick={handleClick}
            aria-label={text.action}
            data-testid="book-again-button"
          >
            <Icon name="FileCopy" size="sm" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{text.action}</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Button variant="outline-primary" onClick={handleClick} data-testid="book-again-button">
      {text.action}
    </Button>
  )
}
